import * as components from 'bootstrap';
import { darkMode, setTheme } from './layout/dark-mode.js';

const bound = new WeakSet();
let delegated = false;
let initialized = false;
let colorModeInitialized = false;
let passwordIdentifier = 0;

// Native components own their state and keyboard behavior. These adapters only
// provide project-specific input decoration, content, and navigation preferences.
export function createRuntime(initializeLayout = () => {}) {
  const api = {
    ...components,
    init(root = document) {
      // Match native automatic data APIs for late-loaded and inserted content.
      const automatic = [
        ['[data-bs-toggle="tooltip"]', components.Tooltip],
        ['[data-bs-toggle="popover"]', components.Popover],
        ['[data-bs-otp]', components.OtpInput],
        ['.form-range', components.Range],
        ['[data-bs-chips]', components.Chips],
        ['[data-bs-toggle="combobox"]', components.Combobox],
        ['[data-bs-strength]', components.Strength],
        ['[data-bs-toggle="nav-overflow"]', components.NavOverflow],
        [
          '[data-bs-toggle="datepicker"][data-bs-inline="true"], [data-bs-toggle="datepicker"][data-bs-selected-dates]',
          components.Datepicker
        ],
        ['[data-bs-spy="scroll"]', components.ScrollSpy],
        ['[data-bs-autoplay="true"]', components.Carousel],
        ['.active[data-bs-toggle="tab"]', components.Tab],
        ['[data-bs-toggle="menu"]', components.Menu]
      ];
      for (const [selector, Component] of automatic) {
        const elements = [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
        elements.forEach((element) => Component.getOrCreateInstance(element));
      }
      if (!colorModeInitialized && root.querySelector('[data-bs-theme-value]')) {
        colorModeInitialized = true;
        darkMode();
      }
      initializeControls(root);
      initializeNavigation(root);
      initializeLayout(root, api);
      initialized = true;
    },
    isInitialized: () => initialized,
    setTheme,
    updateSkin({ primary, secondary } = {}) {
      if (primary) document.documentElement.style.setProperty('--au-primary', primary);
      if (secondary) document.documentElement.style.setProperty('--au-secondary', secondary);
    }
  };
  window.AsteroUI = api;
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', () => api.init(), { once: true });
  else api.init();
  return api;
}

function initializeControls(root) {
  root.querySelectorAll('[data-au-password]').forEach((wrapper) => {
    if (bound.has(wrapper)) return;
    const input = wrapper.querySelector('input');
    const button = wrapper.querySelector('button');
    if (!input || !button) return;
    bound.add(wrapper);
    const oldId = input.id;
    if (!oldId || document.getElementById(oldId) !== input) {
      let uniqueId;
      do {
        uniqueId = `au-password-${++passwordIdentifier}`;
      } while (document.getElementById(uniqueId));
      input.id = uniqueId;
      const label = wrapper.previousElementSibling;
      if (label?.tagName === 'LABEL' && label.htmlFor === oldId) label.htmlFor = input.id;
    }
    button.type = 'button';
    button.setAttribute('data-bs-toggle', 'toggler');
    button.setAttribute('data-bs-target', `#${input.id}`);
    components.Toggler.getOrCreateInstance(input, { attribute: 'type', value: 'text' });
    const update = () => {
      // Toggler removes a matching attribute; restore password's non-default value.
      if (!input.hasAttribute('type')) input.type = 'password';
      const visible = input.type === 'text';
      button.setAttribute('aria-pressed', String(visible));
      button.setAttribute('aria-label', visible ? 'Hide password' : 'Show password');
      const icon = button.querySelector('i');
      if (icon) {
        icon.setAttribute('aria-hidden', 'true');
        icon.classList.toggle('ri-eye-line', visible);
        icon.classList.toggle('ri-eye-off-line', !visible);
      }
    };
    input.addEventListener('toggled.bs.toggler', update);
    update();
  });
  root.querySelectorAll('[data-au-search]').forEach((wrapper) => {
    if (bound.has(wrapper)) return;
    const input = wrapper.querySelector('input');
    const clear = wrapper.querySelector('button');
    if (!input || !clear) return;
    bound.add(wrapper);
    clear.type = 'button';
    clear.setAttribute('aria-label', 'Clear search');
    const update = () => {
      clear.hidden = !input.value;
    };
    input.addEventListener('input', update);
    clear.addEventListener('click', () => {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    });
    update();
  });
  root.querySelectorAll('.color-input-with-text').forEach((wrapper) => {
    if (bound.has(wrapper)) return;
    const input = wrapper.querySelector('input[type="color"]');
    const text = wrapper.querySelector('input[type="text"]');
    if (!input || !text) return;
    bound.add(wrapper);
    const update = () => {
      text.value = input.value.toUpperCase();
    };
    input.addEventListener('input', update);
    update();
  });
  if (!delegated) {
    delegated = true;
    const repairInitialFocus = (event) => {
      const dialog = event.target;
      if (
        !(dialog instanceof window.HTMLDialogElement) ||
        !dialog.matches(':modal') ||
        dialog.contains(document.activeElement)
      )
        return;
      const preferred = dialog.querySelector('[autofocus]');
      const first = [
        ...dialog.querySelectorAll(
          'button:not(:disabled), a[href], input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]'
        )
      ].find((element) => element.getClientRects().length > 0);
      (preferred || first || dialog).focus({ preventScroll: true });
    };
    document.addEventListener('shown.bs.dialog', repairInitialFocus);
    document.addEventListener('shown.bs.drawer', repairInitialFocus);
    const desktop = matchMedia('(min-width: 992px)');
    desktop.addEventListener('change', () => {
      if (!desktop.matches) return;
      document
        .querySelectorAll('dialog[data-au-mobile-nav][open]')
        .forEach((element) => components.Drawer.getOrCreateInstance(element).hide());
    });
    document.addEventListener('show.bs.dialog', (event) => {
      const image = event.target.querySelector('#modalImage');
      const source = event.relatedTarget?.getAttribute('data-bs-image');
      if (image && source) image.src = source;
    });
  }
}

function initializeNavigation(root) {
  // v6 handles submenu hover/click/keyboard natively. Preserve the website's
  // existing top-level pointer hover preference without replacing Menu itself.
  root.querySelectorAll('[data-website-theme] nav [data-bs-toggle="menu"]').forEach((toggle) => {
    if (bound.has(toggle) || toggle.closest('.submenu')) return;
    const parent = toggle.parentElement;
    if (!parent || !parent.querySelector(':scope > .menu')) return;
    bound.add(toggle);
    const menu = components.Menu.getOrCreateInstance(toggle);
    parent.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'touch' && matchMedia('(hover: hover)').matches) menu.show();
    });
    parent.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'touch' && !parent.contains(document.activeElement)) menu.hide();
    });
  });
}
